import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Layers, RefreshCw, Check, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import FooterSection from "@/components/FooterSection";
import { useTranslation } from "@/components/LanguageContext";
import CategoriesSection from "@/components/courses/CategoriesSection";
import RulesSection, { PracticalSection } from "@/components/courses/RulesSection";
import CoursesSubNav from "@/components/courses/CoursesSubNav";

const TRACK_SCROLL_MARGIN = { scrollMarginTop: "240px" };

function TrackBlock({ id, idx, color, icon: Icon, title, desc, features, ctaLabel }) {
  return (
    <div id={id} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start" style={TRACK_SCROLL_MARGIN} data-testid={`track-${id}`}>
      <div className="md:col-span-5">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 shadow-md"
          style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon className="w-8 h-8" style={{ color }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
          Track {String(idx + 1).padStart(2, "0")}
        </p>
        <h2 className="font-['Outfit'] text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1628] mb-4">{title}</h2>
        <p className="text-base text-slate-600 leading-relaxed mb-6">{desc}</p>
        <Link to="/#contacto" className="inline-flex items-center gap-2 text-sm font-bold text-[#0A1628] hover:text-[#00A8FF] transition-colors" data-testid={`cta-${id}`}>
          {ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="md:col-span-7">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          <ul className="space-y-3">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 shrink-0" style={{ backgroundColor: `${color}20` }}>
                  <Check className="w-3 h-3" style={{ color }} />
                </div>
                <span className="text-[15px] text-slate-700 leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// Hook: track active main section + active subtopic via scroll position
function useActiveSection() {
  const [activeSection, setActiveSection] = useState("base");
  const [activeSub, setActiveSub] = useState("");

  useEffect(() => {
    const sections = ["base", "dual", "step", "conv", "rules"];
    const subs = {
      base:  ["g-none", "g-a", "g-b1", "g-b2"],
      rules: ["rules-time", "rules-mods", "rules-docs"],
    };

    const onScroll = () => {
      let current = "base";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 280) current = id;
      }
      setActiveSection(current);

      const subList = subs[current] || [];
      let curSub = subList[0] || "";
      for (const id of subList) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 300) curSub = id;
      }
      setActiveSub(curSub);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { activeSection, activeSub };
}

export default function CoursesPage() {
  const { t, lang } = useTranslation();
  const { activeSection, activeSub } = useActiveSection();

  const subNav = [
    { id: "base", label: t("coursesPage.sub1") },
    { id: "dual", label: t("coursesPage.sub2") },
    { id: "step", label: t("coursesPage.sub5"), glow: true, key: "step" },
    { id: "conv", label: t("coursesPage.sub3") },
    { id: "rules", label: t("coursesPage.sub4") },
  ];

  const secondaryNav = {
    base: [
      { id: "g-none", label: t("coursesPage.subBaseM"),  color: "#94A3B8" },
      { id: "g-a",    label: t("coursesPage.subBaseA"),  color: "#00A8FF" },
      { id: "g-b1",   label: t("coursesPage.subBaseB1"), color: "#F59E0B" },
      { id: "g-b2",   label: t("coursesPage.subBaseB2"), color: "#10B981" },
    ],
    rules: [
      { id: "rules-time",     label: t("coursesPage.subRulesTime"),     color: "#00A8FF" },
      { id: "rules-mods",     label: t("coursesPage.subRulesMods"),     color: "#F59E0B" },
      { id: "rules-docs",     label: t("coursesPage.subRulesDocs"),     color: "#0A1628" },
    ],
  };
  const hasSecondary = !!secondaryNav[activeSection];

  const tracks = [
    {
      id: "base", icon: GraduationCap, color: "#00A8FF",
      title: t("coursesPage.base.title"), desc: t("coursesPage.base.desc"),
      features: [t("coursesPage.base.f1"), t("coursesPage.base.f2"), t("coursesPage.base.f3"), t("coursesPage.base.f4"), t("coursesPage.base.f5"), t("coursesPage.base.f6")],
    },
    {
      id: "dual", icon: Layers, color: "#F59E0B",
      title: t("coursesPage.dual.title"), desc: t("coursesPage.dual.desc"),
      features: [t("coursesPage.dual.f1"), t("coursesPage.dual.f2"), t("coursesPage.dual.f3"), t("coursesPage.dual.f4"), t("coursesPage.dual.f5"), t("coursesPage.dual.f6")],
    },
    {
      id: "conv", icon: RefreshCw, color: "#10B981",
      title: t("coursesPage.conv.title"), desc: t("coursesPage.conv.desc"),
      features: [t("coursesPage.conv.f1"), t("coursesPage.conv.f2"), t("coursesPage.conv.f3"), t("coursesPage.conv.f4"), t("coursesPage.conv.f5"), t("coursesPage.conv.f6")],
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <CoursesSubNav subNav={subNav} secondaryNav={secondaryNav} activeSection={activeSection} activeSub={activeSub} />

      {/* Hero — fixed paddingTop to avoid layout shifts when secondary nav toggles */}
      <section className="bg-[#0A1628] text-white" style={{ paddingTop: "248px", paddingBottom: "80px" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00A8FF] mb-3">L4C Aviation Academy</p>
          <h1 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">{t("coursesPage.hero")}</h1>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">{t("coursesPage.heroDesc")}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 space-y-20">
          {/* Track 01: Base + Categories */}
          <div>
            <TrackBlock idx={0} id={tracks[0].id} color={tracks[0].color} icon={tracks[0].icon} title={tracks[0].title} desc={tracks[0].desc} features={tracks[0].features} ctaLabel={t("coursesPage.cta")} />
            <CategoriesSection t={t} />
          </div>

          {/* Track 02: Dual */}
          <TrackBlock idx={1} id={tracks[1].id} color={tracks[1].color} icon={tracks[1].icon} title={tracks[1].title} desc={tracks[1].desc} features={tracks[1].features} ctaLabel={t("coursesPage.cta")} />

          {/* 🚀 Step-by-step (visually centred between Dual and Conversion) */}
          <PracticalSection t={t} />

          {/* Track 03: Conversion */}
          <TrackBlock idx={2} id={tracks[2].id} color={tracks[2].color} icon={tracks[2].icon} title={tracks[2].title} desc={tracks[2].desc} features={tracks[2].features} ctaLabel={t("coursesPage.cta")} />

          {/* Rules Section */}
          <RulesSection t={t} lang={lang} />
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
